import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { usePermissions } from "@/hooks/usePermissions";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { slugify } from "@/lib/utils";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "@/components/ui/sonner";

interface AgentDefault {
  id: string;
  slug: string;
  nome: string;
  descricao: string | null;
  system_prompt: string | null;
  activation_condition_prompt: string | null;
  base_agent_type: string | null;
  model: string | null;
  temperature: number | null;
  max_tokens: number | null;
  status: string;
  version: number;
}

export default function IAgentDefaults() {
  const { isAdminSaas } = usePermissions();
  const [items, setItems] = useState<AgentDefault[]>([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<AgentDefault | null>(null);
  const [form, setForm] = useState<Partial<AgentDefault>>({ status: "ativo", version: 1 });

  const canManage = useMemo(() => isAdminSaas, [isAdminSaas]);

  useEffect(() => {
    const load = async () => {
      const { data, error } = await supabase.from("ia_agent_defaults" as any).select("*");
      if (error) {
        toast("Erro ao carregar defaults");
      } else {
        setItems((data as any[]) || []);
      }
    };
    load();
  }, []);

  const save = async () => {
    const payload = {
      slug: form.nome ? slugify(form.nome) : form.slug,
      nome: form.nome,
      descricao: form.descricao,
      system_prompt: form.system_prompt,
      activation_condition_prompt: form.activation_condition_prompt,
      base_agent_type: form.base_agent_type,
      model: null,
      temperature: null,
      max_tokens: null,
      status: form.status,
      version: form.version,
    } as any;
    if (editing) {
      const { error } = await supabase.from("ia_agent_defaults" as any).update(payload).eq("id", editing.id);
      if (error) {
        toast("Erro ao atualizar");
        return;
      }
    } else {
      const { error } = await supabase.from("ia_agent_defaults" as any).insert(payload);
      if (error) {
        toast("Erro ao criar");
        return;
      }
    }
    const { data } = await supabase.from("ia_agent_defaults" as any).select("*");
    setItems((data as any[]) || []);
    setOpen(false);
    setEditing(null);
    setForm({ status: "ativo", version: 1 });
  };

  if (!canManage) {
    return (
      <div className="p-6">
        <div className="text-center space-y-2">
          <div className="text-2xl font-bold">Acesso Negado</div>
          <div className="text-muted-foreground">Apenas Admin SaaS pode gerenciar defaults de agentes</div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-4">
      <div className="flex justify-between items-center">
        <div className="text-xl font-semibold">Defaults de Agentes de IA</div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button onClick={() => { setEditing(null); setForm({ status: "ativo", version: 1 }); }}>Novo Default</Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-4xl">
            <DialogHeader>
              <DialogTitle>{editing ? "Editar Default" : "Criar Default"}</DialogTitle>
            </DialogHeader>
            <div className="grid gap-4">
              <label className="text-sm">Nome</label>
              <Input placeholder="Nome" value={form.nome || ""} onChange={(e) => setForm({ ...form, nome: e.target.value })} />
              {form.nome ? (
                <p className="text-xs text-muted-foreground">Slug: <code className="font-mono">{slugify(form.nome)}</code></p>
              ) : null}
              <label className="text-sm">Descrição</label>
              <Textarea placeholder="Descrição" value={form.descricao || ""} onChange={(e) => setForm({ ...form, descricao: e.target.value })} />
              <label className="text-sm">System Prompt</label>
              <Textarea placeholder="System Prompt" value={form.system_prompt || ""} onChange={(e) => setForm({ ...form, system_prompt: e.target.value })} />
              <label className="text-sm">Activation Condition Prompt</label>
              <Textarea placeholder="Activation Condition Prompt" value={form.activation_condition_prompt || ""} onChange={(e) => setForm({ ...form, activation_condition_prompt: e.target.value })} />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                <div>
                  <label className="text-sm">Tipo Base</label>
                  <Select value={form.base_agent_type || ""} onValueChange={(v) => setForm({ ...form, base_agent_type: v })}>
                    <SelectTrigger><SelectValue placeholder="Tipo Base" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="assistant">assistant</SelectItem>
                      <SelectItem value="router">router</SelectItem>
                      <SelectItem value="tool_executor">tool_executor</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm">Status</label>
                  <Select value={form.status || "ativo"} onValueChange={(v) => setForm({ ...form, status: v })}>
                    <SelectTrigger><SelectValue placeholder="Status" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ativo">ativo</SelectItem>
                      <SelectItem value="inativo">inativo</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                <div>
                  <label className="text-sm">Versão</label>
                  <Input placeholder="Versão" type="number" value={form.version?.toString() || "1"} onChange={(e) => setForm({ ...form, version: Number(e.target.value) })} />
                </div>
              </div>
              <Button onClick={save}>Salvar</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Slug</TableHead>
            <TableHead>Nome</TableHead>
            <TableHead>Tipo</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Ações</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {items.map((it) => (
            <TableRow key={it.id}>
              <TableCell>{it.slug}</TableCell>
              <TableCell>{it.nome}</TableCell>
              <TableCell>{it.base_agent_type}</TableCell>
              <TableCell>{it.status}</TableCell>
              <TableCell>
                <Button variant="secondary" onClick={() => { setEditing(it); setForm(it); setOpen(true); }}>Editar</Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
